import axios from 'axios';
import AuthService from './AuthService';

const BASE = process.env.REACT_APP_API_URL;
const hdr = () => ({ headers: { Authorization: `Bearer ${AuthService.getToken()}` } });

/** ASP.NET may return a bare array or legacy `{ $values: [...] }`. */
function unwrapArrayPayload(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.$values)) return data.$values;
  return [];
}

function normalizeIntList(raw) {
  return unwrapArrayPayload(raw).map(Number).filter((n) => !Number.isNaN(n));
}

/** Strip ReferenceHandler metadata and build a clean PUT body (PascalCase for .NET binding). */
export function buildSchedulerSettingPayload(setting, clientId) {
  const rp = setting.retryPolicy ?? setting.RetryPolicy ?? {};
  const definitionId = Number(
    setting.schedulerDefinitionId ?? setting.SchedulerDefinitionId ?? 0
  );
  return {
    Id: Number(setting.id ?? setting.Id ?? 0),
    ClientId: Number(clientId),
    SchedulerDefinitionId: definitionId,
    IsEnabled: !!(setting.isEnabled ?? setting.IsEnabled),
    Channels: normalizeIntList(setting.channels ?? setting.Channels) || [1],
    ReminderMinutesBefore: Number(
      setting.reminderMinutesBefore ?? setting.ReminderMinutesBefore ?? 30
    ),
    RepeatIntervalDays: Number(
      setting.repeatIntervalDays ?? setting.RepeatIntervalDays ?? 2
    ),
    BusinessHoursOnly: !!(setting.businessHoursOnly ?? setting.BusinessHoursOnly),
    BusinessHoursStart: setting.businessHoursStart ?? setting.BusinessHoursStart ?? '09:00',
    BusinessHoursEnd: setting.businessHoursEnd ?? setting.BusinessHoursEnd ?? '19:00',
    Priority: Number(setting.priority ?? setting.Priority ?? 2),
    RetryPolicy: {
      MaxAttempts: Number(rp.maxAttempts ?? rp.MaxAttempts ?? 3),
      DelaysMinutes: normalizeIntList(rp.delaysMinutes ?? rp.DelaysMinutes) || [5, 30],
    },
    TimeZoneId: setting.timeZoneId ?? setting.TimeZoneId ?? 'India Standard Time',
  };
}

const jsonHdr = () => ({
  headers: {
    Authorization: `Bearer ${AuthService.getToken()}`,
    'Content-Type': 'application/json',
  },
});

const NotificationEngineService = {
  getDefinitions: () =>
    axios.get(`${BASE}/NotificationEngine/definitions`, hdr()).then((r) => unwrapArrayPayload(r.data)),

  getSettings: (clientId) =>
    axios.get(`${BASE}/NotificationEngine/settings/${clientId}`, hdr()).then((r) => unwrapArrayPayload(r.data)),

  saveSetting: (setting, clientId) =>
    axios
      .put(`${BASE}/NotificationEngine/settings`, buildSchedulerSettingPayload(setting, clientId), jsonHdr())
      .then((r) => r.data),

  setEnabled: (id, enabled) =>
    axios.patch(`${BASE}/NotificationEngine/settings/${id}/enabled`, enabled, hdr()).then((r) => r.data),

  getDashboard: (clientId) =>
    axios
      .get(`${BASE}/NotificationEngine/dashboard${clientId ? `?clientId=${clientId}` : ''}`, hdr())
      .then((r) => r.data),

  getQueue: (clientId, status) => {
    const params = new URLSearchParams();
    if (clientId) params.set('clientId', clientId);
    if (status) params.set('status', status);
    const q = params.toString();
    return axios
      .get(`${BASE}/NotificationEngine/queue${q ? `?${q}` : ''}`, hdr())
      .then((r) => unwrapArrayPayload(r.data));
  },

  getHistory: (clientId) =>
    axios
      .get(`${BASE}/NotificationEngine/history${clientId ? `?clientId=${clientId}` : ''}`, hdr())
      .then((r) => unwrapArrayPayload(r.data)),

  retry: (id) =>
    axios.post(`${BASE}/NotificationEngine/queue/${id}/retry`, {}, hdr()).then((r) => r.data),

  runSchedulers: (clientId) =>
    axios
      .post(`${BASE}/NotificationEngine/scheduler/run${clientId ? `?clientId=${clientId}` : ''}`, {}, hdr())
      .then((r) => unwrapArrayPayload(r.data)),

  getTemplates: (clientId) =>
    axios
      .get(`${BASE}/NotificationEngine/templates${clientId ? `?clientId=${clientId}` : ''}`, hdr())
      .then((r) => unwrapArrayPayload(r.data)),

  saveTemplate: (dto) =>
    axios.put(`${BASE}/NotificationEngine/templates`, dto, hdr()).then((r) => r.data),

  getInApp: (unreadOnly = false) =>
    axios
      .get(`${BASE}/NotificationEngine/in-app?unreadOnly=${unreadOnly}`, hdr())
      .then((r) => unwrapArrayPayload(r.data)),

  markRead: (id) =>
    axios.patch(`${BASE}/NotificationEngine/in-app/${id}/read`, {}, hdr()).then((r) => r.data),

  getPreferences: () =>
    axios.get(`${BASE}/NotificationEngine/preferences`, hdr()).then((r) => r.data),

  savePreferences: (dto) =>
    axios.put(`${BASE}/NotificationEngine/preferences`, dto, hdr()).then((r) => r.data),

  getPlatformSettings: () =>
    axios.get(`${BASE}/PlatformSettings`, hdr()).then((r) => r.data),

  updatePlatformSettings: (dto) =>
    axios.put(`${BASE}/PlatformSettings`, dto, jsonHdr()).then((r) => r.data),
};

export default NotificationEngineService;
